import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  KeyRound, Loader2, Fingerprint, Mail, Lock, ExternalLink, Info,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function MasterAdminDialog({ open, onOpenChange }) {
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    new_email: "", new_password: "", confirm_password: "", current_password: "",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-admin/status");
      setStatus(data);
      setForm((f) => ({ ...f, new_email: data.email || "", new_password: "", confirm_password: "", current_password: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load master admin status");
      onOpenChange(false);
    }
    setLoading(false);
  }, [onOpenChange]);
  useEffect(() => { if (open) reload(); }, [open, reload]);

  const submit = async () => {
    const trimmedEmail = (form.new_email || "").trim().toLowerCase();
    const emailChanged = status && trimmedEmail && trimmedEmail !== (status.email || "").toLowerCase();
    const pwChanged = !!form.new_password;
    if (!emailChanged && !pwChanged) {
      toast.error("Change the email or password before saving");
      return;
    }
    if (pwChanged && form.new_password.length < 12) {
      toast.error("New password must be at least 12 characters");
      return;
    }
    if (pwChanged && form.new_password !== form.confirm_password) {
      toast.error("Password confirmation does not match");
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      if (emailChanged) payload.new_email = trimmedEmail;
      if (pwChanged) payload.new_password = form.new_password;

      const { data } = await api.post("/master-admin/update", payload);
      const bits = [];
      if (data.changed?.includes("email")) bits.push("email");
      if (data.changed?.includes("password_hash")) bits.push("password");
      toast.success(`Master admin ${bits.join(" & ")} updated`);
      onOpenChange(false);
      if (checkAuth) await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="master-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-sky-600" />Master Admin Settings
          </DialogTitle>
          <DialogDescription>
            Change your master admin email or password. When a YubiKey is
            registered, a hardware touch is required to authorise changes.
          </DialogDescription>
        </DialogHeader>

        {loading || !status ? (
          <div className="py-8 text-center text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
          </div>
        ) : !status.is_master ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-sm text-amber-800">
              This dialog is only available to the master admin account.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Simplified mode notice */}
            <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] text-amber-800"
              data-testid="master-admin-simple-mode-notice">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Simple mode: any admin session can update these details. Current-password
                and YubiKey re-auth will be re-enabled in a future security hardening pass.
              </span>
            </div>

            {/* email + password */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600 flex items-center gap-1 mb-1">
                  <Mail className="h-3 w-3" />Master admin email
                </label>
                <Input value={form.new_email}
                  onChange={(e) => setForm({ ...form, new_email: e.target.value })}
                  data-testid="master-admin-email-input"
                  placeholder="admin@icebergdigital.com.au" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-zinc-600 flex items-center gap-1 mb-1">
                    <Lock className="h-3 w-3" />New password
                  </label>
                  <Input type="password" value={form.new_password}
                    onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                    data-testid="master-admin-new-password-input"
                    placeholder="min. 12 characters (blank = keep current)" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 mb-1 block">
                    Confirm new password
                  </label>
                  <Input type="password" value={form.confirm_password}
                    onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                    data-testid="master-admin-confirm-password-input"
                    placeholder="repeat new password" />
                </div>
              </div>
            </div>

            {/* YubiKey management shortcut */}
            <Card className="border-zinc-200 bg-zinc-50">
              <CardContent className="p-3 text-xs text-zinc-600 space-y-2">
                <div className="font-semibold flex items-center gap-1.5">
                  <Fingerprint className="h-3.5 w-3.5" />YubiKey master authentication
                </div>
                {status.has_yubikeys ? (
                  <>
                    <div>
                      <span className="text-emerald-700 font-medium">Enabled.</span>{" "}
                      All email/password changes are gated on a physical YubiKey touch.
                    </div>
                    <a href="/security-stats?tab=yubikey"
                      data-testid="master-admin-yubikey-manage-link"
                      className="inline-flex items-center gap-1 text-sky-700 hover:underline"
                      onClick={() => onOpenChange(false)}>
                      Manage security keys<ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-amber-700 font-medium">Not enrolled.</span>{" "}
                      Register a YubiKey to elevate this account to hardware-token authentication.
                      Once enrolled, changes here will require a physical touch instead of a password.
                    </div>
                    <a href="/security-stats?tab=yubikey"
                      data-testid="master-admin-yubikey-enroll-link"
                      className="inline-flex items-center gap-1 text-sky-700 hover:underline"
                      onClick={() => onOpenChange(false)}>
                      Enrol a YubiKey<ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="master-admin-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !status?.is_master}
            data-testid="master-admin-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {status?.yubikey_required_for_updates ? "Save with YubiKey touch" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
